import { FC, useState } from 'react';
import type { UserProject } from '@teachur/http-client';
import { Drawer } from '@new/common/ui/Drawer/Drawer';
import { ProjectDrawerBody } from './ProjectsDrawerBody';
import { ProjectDrawerHeader } from './ProjectsDrawerHeader';
import { ProjectDrawerSidePanel } from './ProjectDrawerSidePanel';
import { getLatestSubmission } from '../../utils/projectReviewDrawerHelpers';

type ActiveSidePanel = 'submissionsHistory' | 'filePreview' | 'courseDetails' | null;

interface ProjectsDrawerProps {
  selectedProject: UserProject | null;
  setSelectedProjectId: (id: number | null) => void;
}

export const ProjectsDrawer: FC<ProjectsDrawerProps> = ({
  selectedProject,
  setSelectedProjectId,
}) => {
  const [activeSidePanel, setActiveSidePanel] = useState<ActiveSidePanel>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);

  const handleClose = () => {
    setSelectedProjectId(null);
    setActiveSidePanel(null);
    setSelectedCourseId(null);
  };

  const onToggleSubmissionsHistory = () => {
    setActiveSidePanel((prev) => (prev === 'submissionsHistory' ? null : 'submissionsHistory'));
  };

  const onToggleFilePreview = () => {
    setActiveSidePanel((prev) => (prev === 'filePreview' ? null : 'filePreview'));
  };

  const onOpenCourseDetails = (courseId: number) => {
    setSelectedCourseId(courseId);
    setActiveSidePanel('courseDetails');
  };

  const onCloseCourseDetails = () => {
    setActiveSidePanel(null);
    setSelectedCourseId(null);
  };

  const latestSubmission = getLatestSubmission(selectedProject);

  const isSidePanelOpen =
    activeSidePanel === 'submissionsHistory' ||
    (activeSidePanel === 'filePreview' && !!latestSubmission?.file) ||
    activeSidePanel === 'courseDetails';

  return (
    <Drawer
      open={selectedProject !== null}
      onClose={handleClose}
      header={
        <ProjectDrawerHeader
          project={selectedProject}
          onClose={handleClose}
          onOpenSubmissionsHistory={onToggleSubmissionsHistory}
        />
      }
      className="max-w-130!"
      sidePanel={
        <ProjectDrawerSidePanel
          project={selectedProject}
          isSubmissionsHistoryOpen={activeSidePanel === 'submissionsHistory'}
          isFilePreviewOpen={activeSidePanel === 'filePreview'}
          courseId={selectedCourseId ?? undefined}
          onCloseSubmissionsHistory={() => setActiveSidePanel(null)}
          onCloseFilePreview={() => setActiveSidePanel(null)}
          onCloseCourseDetails={onCloseCourseDetails}
        />
      }
      isSidePanelOpen={isSidePanelOpen}
    >
      <ProjectDrawerBody
        project={selectedProject}
        onOpenFilePreview={onToggleFilePreview}
        onOpenCourseDetails={onOpenCourseDetails}
      />
    </Drawer>
  );
};
